import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { EngineState } from './state/engine.state';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { ViewTreeNode } from './models/view.tree-node';
import { EngineService } from './engine.service';
import { isNil } from 'lodash';
import { ObjectColor } from '../shared/object-color';
import { EngineColorRecipeModel } from '../edit/models/engine-color-recipe.model';
import { ColorRecipeModel } from '../edit/models/color-recipe.model';
import { ObjectId } from './state/nodes/engine.nodes.state.model';
import { HumanInterface } from './models/human.interface';

declare const Human: HumanInterface;

@Injectable({
  providedIn: 'root',
})
export class PaintService {
  constructor(
    private store: Store,
    private graphService: BdGraphService,
    private engineService: EngineService,
  ) {}

  highlightDisabled = false;

  colorObject(objectId: string, params: EngineColorRecipeModel) {
    const { tintColor, saturation, brightness, contrast, opacity } = params;
    const humanObject = Human.scene.objects[objectId];

    if (humanObject instanceof Human.scene.Object) {
      // to clear or not to clear, that is the question
      // apply mod properties that are not default
      if (!isNil(tintColor)) {
        humanObject.setTintColor(tintColor);
      }
      if (!isNil(saturation)) {
        humanObject.setSaturation(saturation);
      }
      if (!isNil(brightness)) {
        humanObject.setBrightness(brightness);
      }
      if (!isNil(contrast)) {
        humanObject.setContrast(contrast);
      }
      if (!isNil(opacity)) {
        humanObject.setOpacity(opacity);
      }
    }
  }

  getColor(objectId: string): EngineColorRecipeModel {
    return ObjectColor.getColorRecipe(Human, objectId);
  }

  uncolorObject(objectId: string) {
    const object = Human.scene.objects[objectId];
    if (object instanceof Human.scene.Object) {
      object.clearColorModifiers();
      object.clearOpacityModifiers();
    }
  }

  unpaint(node: ViewTreeNode) {
    // relay color mods to each engine leaf
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    this.graphService.getLeafDescendants(viewGraph, node.id).forEach((id) => {
      this.engineService.getObjects(id).forEach((humanObjectId: string) => {
        const humanObject = Human.scene.objects[humanObjectId];
        if (humanObject instanceof Human.scene.Object) {
          // reset object mod properties
          humanObject.clearColorModProperties();
          humanObject.clearOpacityModProperties();
        }
      });
    });
  }

  paint(objectId: string, recipe: ColorRecipeModel) {
    // relay color mods to each engine leaf
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const engineRecipe = ColorRecipeModel.toEngine(recipe);

    this.graphService.getLeafDescendants(viewGraph, objectId).forEach((id) => {
      this.engineService.getObjects(id).forEach((humanObjectId: string) => {
        this.colorObject(humanObjectId, engineRecipe);
      });
    });
  }

  highlight(objectId: string, highlight: boolean) {
    // allow un-highlight if highlight is disabled
    if (this.highlightDisabled === false || highlight === false) {
      // relay highlight to each engine leaf
      const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
      this.graphService.getLeafDescendants(viewGraph, objectId).forEach((id) => {
        this.engineService.getObjects(id).forEach((humanObjectId: string) => {
          const humanObject = Human.scene.objects[humanObjectId];
          if (humanObject instanceof Human.scene.Object) {
            humanObject.setHighlight(highlight);
          }
        });
      });
    }
  }

  xRay(node: ViewTreeNode, xRay: boolean) {
    const object = Human.scene.objects[node.id];
    object.setXRay(xRay);
  }

  paintAll(objectIds: ObjectId[], recipe: ColorRecipeModel) {
    objectIds.forEach((id) => this.paint(id, recipe));
  }

  unpaintAll(nodes: ViewTreeNode[]) {
    nodes.forEach((node) => {
      this.unpaint(node);
    });
  }

  highlightAll(objectIds: ObjectId[], highlight: boolean) {
    objectIds.forEach((id) => this.highlight(id, highlight));
  }

  disableHighlight() {
    this.highlightDisabled = true;
  }

  enableHighlight() {
    this.highlightDisabled = false;
  }

  /**
   * Sets the selected object's highlight color
   * @param color RGB decimal values
   */
  setHighlightColor(color: [number, number, number]) {
    Human.renderer.highlightColor = [...color];
  }
}
